import {
  apiContainer,
  queryBuilder,
  massageSingleRecordBeforeUpsert
} from './apiContainer'
import { ArtificialDetailPropertyNames } from '../../common/globalNotary'
import uuid from 'react-uuid'

const SLICE_API_ROOT = '/parties'

const partyApi = apiContainer.injectEndpoints({
  endpoints: build => ({
    listParties: build.query({
      query: args => queryBuilder(SLICE_API_ROOT, args)
    }),
    listUsers: build.query({
      query: args => queryBuilder(`${SLICE_API_ROOT}/~/users`, args)
    }),
    getOneFatParty: build.query({
      async queryFn (_arg, _queryApi, _extraOptions, fetchWithBQ) {

        try
        {

          const fetchCallArray = [fetchWithBQ(`${SLICE_API_ROOT}/${_arg.id}`)]
          fetchCallArray.push(fetchWithBQ(`/groups?party_id=${_arg.id}`))
          fetchCallArray.push(fetchWithBQ(`/groups?owner_id=${_arg.id}`))
          fetchCallArray.push(fetchWithBQ(`/contracts?party_id=${_arg.id}`))
          fetchCallArray.push(fetchWithBQ(`/assets?party_id=${_arg.id}`))
          fetchCallArray.push(
            fetchWithBQ(`/parties/~/skillsets?party_id=${_arg.id}`)
          )

          fetchCallArray.push(fetchWithBQ(`/skillsets`))

          const allInOne = await Promise.all(fetchCallArray)

          const party = allInOne[0].data.data
          const groups = allInOne[1].data.data
          const ownerOfGroups = allInOne[2].data.data
          const accounts = allInOne[3].data.data
          const locations = allInOne[4].data.data
          const skillsets = allInOne[5].data.data
          const fullSkillsets = allInOne[6].data.data

          if (party?._has_user_account) {
            const { data: { data: user } } = await fetchWithBQ(
              `${SLICE_API_ROOT}/${_arg.id}/users`
            )

            party.user = user
            // The detail form edits user-account status, not the underlying party status.
            party.suspended_at = user.suspended_at ?? null
          }

          const decoratedSkillsets = skillsets.map(o => {
            const lookup = fullSkillsets.find(f => {
              return f.id === o.skillset_id
            })
            if (!lookup) { o['name'] = 'Deleted / Suspended Skillset'}
            else{
              o['name'] = lookup.name
            }
            return o
          })

          if (
            party?.user?.identities?.[0]?.email &&
            !party.email
          ) {
            party.email = party.user?.identities[0].email
          }

          if (!party.email) {
            party.email = party.user?.identities
              ?.find(i => i.type === 'email')
              ?.value
              || null
          }

          party[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1] =
            groups
          party[
            ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1_INITIAL_VALUES
          ] = groups

          party[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2] =
            ownerOfGroups

          party[
            ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2_INITIAL_VALUES
          ] = ownerOfGroups

          party[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3] =
            accounts

          party[
            ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3_INITIAL_VALUES
          ] = accounts

          party[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4] =
            locations

          party[
            ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4_INITIAL_VALUES
          ] = locations

          party[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY5] =
            decoratedSkillsets

          party[
            ArtificialDetailPropertyNames.DETACHED_DEPENDENCY5_INITIAL_VALUES
          ] = decoratedSkillsets

          // tmp fix for saving auxiliary updates
          return { data: { data: { ...party, _objectId: uuid() } } }
        }catch(err)
        {
          console.log(err)
        }
      }
    }),

    listSkillsetForParty: build.query({
      query: partyId => queryBuilder(`/parties/${partyId}/skillsets`),
      forceRefetch: true
    }),
    listPartyTypes: build.query({
      query: () => `${SLICE_API_ROOT}/~/types`,
      keepUnusedDataFor: 86400
    }),
    bulkUpdateParties: build.mutation({
      query: args => {
        return {
          url: SLICE_API_ROOT,
          method: 'PATCH',
          body: { data: args }
        }
      }
    }),
    bulkUpdateUsers: build.mutation({
      query: args => {
        return {
          url: `${SLICE_API_ROOT}/~/users`,
          method: 'PATCH',
          body: { data: args }
        }
      }
    }),
    assertUserForParty: build.mutation({
      query: args => {
        return {
          url: `${SLICE_API_ROOT}/${args.id}/users`,
          method: 'PUT',
          body: args
        }
      }
    }),
    upsertParty: build.mutation({
      query: args => {
        const { id } = args
        const actualArgs = massageSingleRecordBeforeUpsert(args)

        if (!actualArgs.type_id) actualArgs.type_id = null
        if (!actualArgs.job_title_id) actualArgs.job_title_id = null

        delete actualArgs._last_signin
        delete actualArgs._has_user_account
        delete actualArgs.identities
        delete actualArgs.category
        delete actualArgs.skillset
        delete actualArgs.suspended_at
        delete actualArgs.user
        delete actualArgs.type
        delete actualArgs.assertGuest
        delete actualArgs.assertUser
        delete actualArgs.base_type
        delete actualArgs.settings

        if (
          actualArgs.addresses &&
          actualArgs.addresses[0] &&
          !actualArgs.addresses[0].unit
        )
          actualArgs.addresses[0].unit = null

        if (actualArgs.base_type === 'user')
          actualArgs.identities = [
            { type: 'email', identity: actualArgs.email }
          ]

        if (id) {
          // update
          return {
            url: `${SLICE_API_ROOT}/${id}`,
            method: 'PUT',
            body: actualArgs
          }
        } //insert
        else {
          return {
            url: SLICE_API_ROOT,
            method: 'POST',
            body: actualArgs
          }
        }
      }
    }),
    updateSkillsetForParties: build.mutation({
      query: args => {
        return {
          url: `${SLICE_API_ROOT}/~/skillsets`,
          method: 'PUT',
          body: { data: args }
        }
      }
    }),
    deleteParty: build.mutation({
      query: args => {
        return {
          url: `${SLICE_API_ROOT}/${args.id}`,
          method: 'DELETE'
        }
      }
    }),
    deleteUser: build.mutation({
      query: args => {
        return {
          url: `${SLICE_API_ROOT}/${args.id}/users`,
          method: 'DELETE'
        }
      }
    })
  }),
  overrideExisting: true
})

export const {
  useGetOneFatPartyQuery,
  useLazyListPartiesQuery,
  useLazyListUsersQuery,
  useLazyListPartyTypesQuery,
  useLazyListSkillsetForPartyQuery,
  useUpsertPartyMutation,
  useDeletePartyMutation,
  useDeleteUserMutation,
  useBulkUpdatePartiesMutation,
  useBulkUpdateUsersMutation,
  useUpdateSkillsetForPartiesMutation,
  useAssertUserForPartyMutation
} = partyApi
